/**
 * AnimationSystem - Handles visual animations for game events
 * Provides spawn, freeze, collision, and other visual feedback animations
 */

import Phaser from 'phaser';

/**
 * Animation configuration
 */
export interface AnimationConfig {
  /** Duration of spawn animation in ms */
  spawnDuration: number;
  /** Duration of freeze enter animation in ms */
  freezeEnterDuration: number;
  /** Duration of collision impact animation in ms */
  collisionDuration: number;
  /** Duration of fall/destroy animation in ms */
  fallDuration: number;
  /** Duration of win celebration animation in ms */
  winCelebrationDuration: number;
}

export const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
  spawnDuration: 200,
  freezeEnterDuration: 150,
  collisionDuration: 50,
  fallDuration: 600,
  winCelebrationDuration: 200,
};

/**
 * Animation system for visual feedback
 */
export class AnimationSystem {
  private scene: Phaser.Scene;
  private config: AnimationConfig;
  private activeGlows: Map<Phaser.GameObjects.Graphics, Phaser.FX.Glow> = new Map();

  constructor(scene: Phaser.Scene, config: Partial<AnimationConfig> = {}) {
    this.scene = scene;
    this.config = { ...DEFAULT_ANIMATION_CONFIG, ...config };
  }

  /**
   * Play spawn animation (scale from 0 with bounce)
   */
  playSpawnAnimation(
    graphics: Phaser.GameObjects.Graphics,
    onComplete?: () => void
  ): void {
    graphics.setScale(0);
    graphics.setAlpha(0);

    this.scene.tweens.add({
      targets: graphics,
      scale: 1,
      alpha: 1,
      duration: this.config.spawnDuration,
      ease: 'Back.easeOut',
      onComplete: () => onComplete?.(),
    });
  }

  /**
   * Play animation when entering freeze mode
   */
  playFreezeEnterAnimation(graphics: Phaser.GameObjects.Graphics): void {
    // Try to add glow effect if postFX is available
    if (graphics.postFX) {
      try {
        const glow = graphics.postFX.addGlow(0xffffff, 0, 0, false, 0.1, 16);
        this.activeGlows.set(graphics, glow);

        this.scene.tweens.add({
          targets: glow,
          outerStrength: 4,
          duration: this.config.freezeEnterDuration,
          ease: 'Quad.easeOut',
        });
      } catch {
        // PostFX not available, skip glow
      }
    }

    // Subtle scale pulse
    this.scene.tweens.add({
      targets: graphics,
      scale: 1.03,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Play animation when exiting freeze mode
   */
  playFreezeExitAnimation(graphics: Phaser.GameObjects.Graphics): void {
    // Remove glow effect
    const glow = this.activeGlows.get(graphics);
    if (glow && graphics.postFX) {
      this.scene.tweens.add({
        targets: glow,
        outerStrength: 0,
        duration: 100,
        onComplete: () => {
          graphics.postFX?.clear();
          this.activeGlows.delete(graphics);
        },
      });
    }

    // Reset scale
    graphics.setScale(1);
  }

  /**
   * Play collision impact animation (squash and stretch)
   */
  playCollisionImpact(graphics: Phaser.GameObjects.Graphics): void {
    this.scene.tweens.add({
      targets: graphics,
      scaleY: 0.92,
      scaleX: 1.08,
      duration: this.config.collisionDuration,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  /**
   * Play animation when a piece falls off (spin and fade)
   */
  playFallAnimation(
    graphics: Phaser.GameObjects.Graphics,
    onComplete?: () => void
  ): void {
    this.scene.tweens.add({
      targets: graphics,
      rotation: graphics.rotation + Math.PI * 2,
      alpha: 0,
      scale: 0.5,
      duration: this.config.fallDuration,
      ease: 'Quad.easeIn',
      onComplete: () => {
        graphics.destroy();
        onComplete?.();
      },
    });
  }

  /**
   * Play placement success animation
   */
  playPlacementSuccess(graphics: Phaser.GameObjects.Graphics): void {
    this.scene.tweens.add({
      targets: graphics,
      scale: 1.05,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Play win celebration animation for all pieces
   */
  playWinCelebration(
    graphicsList: Phaser.GameObjects.Graphics[],
    onComplete?: () => void
  ): void {
    let delay = 0;

    graphicsList.forEach((graphics, index) => {
      // Wave-like scale animation
      this.scene.tweens.add({
        targets: graphics,
        scale: 1.15,
        duration: this.config.winCelebrationDuration,
        delay,
        yoyo: true,
        ease: 'Sine.easeInOut',
        onComplete: index === graphicsList.length - 1 ? onComplete : undefined,
      });

      // Subtle rotation
      this.scene.tweens.add({
        targets: graphics,
        rotation: graphics.rotation + 0.1,
        duration: this.config.winCelebrationDuration * 2,
        delay,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });

      delay += 50;
    });
  }

  /**
   * Play lose/collapse animation for all pieces
   */
  playCollapseAnimation(
    graphicsList: Phaser.GameObjects.Graphics[],
    onComplete?: () => void
  ): void {
    let delay = 0;
    let completed = 0;

    graphicsList.forEach((graphics) => {
      this.scene.tweens.add({
        targets: graphics,
        alpha: 0.5,
        scale: 0.95,
        duration: 200,
        delay,
        ease: 'Quad.easeIn',
        onComplete: () => {
          completed++;
          if (completed === graphicsList.length) {
            onComplete?.();
          }
        },
      });

      delay += 30;
    });
  }

  /**
   * Play shake animation (for near-collapse feedback)
   */
  playShakeAnimation(graphics: Phaser.GameObjects.Graphics): void {
    const originalX = graphics.x;

    this.scene.tweens.add({
      targets: graphics,
      x: originalX - 3,
      duration: 30,
      yoyo: true,
      repeat: 3,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        graphics.setX(originalX);
      },
    });
  }

  /**
   * Play pulse animation (for highlighting)
   */
  playPulseAnimation(
    graphics: Phaser.GameObjects.Graphics,
    repeat: number = -1
  ): Phaser.Tweens.Tween {
    return this.scene.tweens.add({
      targets: graphics,
      alpha: 0.7,
      scale: 1.05,
      duration: 500,
      yoyo: true,
      repeat,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Stop pulse animation
   */
  stopPulseAnimation(graphics: Phaser.GameObjects.Graphics): void {
    this.scene.tweens.killTweensOf(graphics);
    graphics.setAlpha(1);
    graphics.setScale(1);
  }

  /**
   * Create floating score popup
   */
  createScorePopup(x: number, y: number, points: number, color: number = 0x4caf50): void {
    const popup = this.scene.add.text(x, y - 10, `+${points}`, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '22px',
      color: `#${color.toString(16).padStart(6, '0')}`,
      fontStyle: 'bold',
      stroke: '#ffffff',
      strokeThickness: 3,
    });
    popup.setOrigin(0.5);
    popup.setDepth(150);

    this.scene.tweens.add({
      targets: popup,
      y: y - 70,
      alpha: 0,
      scale: 1.3,
      duration: 900,
      ease: 'Quad.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  /**
   * Clear all active glow effects
   */
  clearGlows(): void {
    this.activeGlows.forEach((_glow, graphics) => {
      if (graphics.postFX) {
        graphics.postFX.clear();
      }
    });
    this.activeGlows.clear();
  }

  /**
   * Destroy and cleanup
   */
  destroy(): void {
    this.clearGlows();
  }
}
